
import React, { useState, useEffect } from 'react';
import {
  GitBranch,
  GitCommit,
  GitPullRequest,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Save
} from 'lucide-react';
import { gitService } from '../services/api';
import toast from 'react-hot-toast';

function GitManager() {
  const [gitStatus, setGitStatus] = useState(null);
  const [commits, setCommits] = useState([]);
  const [diff, setDiff] = useState('');
  const [commitMessage, setCommitMessage] = useState('');
  const [loading, setLoading] = useState({ status: true, log: true, diff: false });
  const [actionLoading, setActionLoading] = useState('');
  const [showForceCloneModal, setShowForceCloneModal] = useState(false);

  const handleClone = async () => {
    try {
      setActionLoading('clone');
      const data = await gitService.clone();
      if (data.alreadyCloned) {
        // Server returned 409 — ask user whether to override
        setShowForceCloneModal(true);
        return;
      }
      toast.success('Repository cloned successfully');
      loadGitData();
    } catch (error) {
      toast.error('Failed to clone repository');
      console.error('Git clone error:', error);
    } finally {
      setActionLoading('');
    }
  };

  const handleForceClone = async () => {
    setShowForceCloneModal(false);
    try {
      setActionLoading('clone');
      await gitService.clone(true);
      toast.success('Repository re-cloned successfully');
      loadGitData();
    } catch (error) {
      toast.error('Failed to force clone repository');
      console.error('Git force clone error:', error);
    } finally {
      setActionLoading('');
    }
  };

  useEffect(() => {
    loadGitData();
  }, []);

  const loadGitData = async () => {
    try {
      const [statusData, logData] = await Promise.allSettled([
        gitService.getStatus(),
        gitService.getLog(10)
      ]);

      if (statusData.status === 'fulfilled') {
        setGitStatus(statusData.value.status);
      }

      if (logData.status === 'fulfilled') {
        setCommits(logData.value.commits);
      }
    } catch (error) {
      toast.error('Failed to load git data');
      console.error('Git load error:', error);
    } finally {
      setLoading({ status: false, log: false, diff: false });
    }
  };

  const loadDiff = async () => {
    try {
      setLoading(prev => ({ ...prev, diff: true }));
      const data = await gitService.getDiff();
      setDiff(data.diff);
    } catch (error) {
      toast.error('Failed to load git diff');
      console.error('Git diff error:', error);
    } finally {
      setLoading(prev => ({ ...prev, diff: false }));
    }
  };

  const handleCommit = async () => {
    if (!commitMessage.trim()) {
      toast.error('Commit message is required');
      return;
    }

    try {
      setActionLoading('commit');
      const data = await gitService.commit(commitMessage);

      if (data.result.committed) {
        toast.success('Changes committed successfully');
        setCommitMessage('');
        loadGitData();
        setDiff('');
      } else {
        toast.info(data.result.message);
      }
    } catch (error) {
      toast.error('Failed to commit changes');
      console.error('Git commit error:', error);
    } finally {
      setActionLoading('');
    }
  };

  const handlePull = async () => {
    try {
      setActionLoading('pull');
      const data = await gitService.pull();
      toast.success('Pulled changes successfully');
      loadGitData();
    } catch (error) {
      toast.error('Failed to pull changes');
      console.error('Git pull error:', error);
    } finally {
      setActionLoading('');
    }
  };

  const handlePush = async () => {
    try {
      setActionLoading('push');
      const data = await gitService.push();
      toast.success('Pushed changes successfully');
      loadGitData();
    } catch (error) {
      toast.error('Failed to push changes');
      console.error('Git push error:', error);
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Git Manager
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage git repository operations and view commit history
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-2">
          <button
            onClick={loadGitData}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleClone}
            disabled={actionLoading === 'clone'}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
          >
            {actionLoading === 'clone' ? (
              <div className="loading-spinner mr-2"></div>
            ) : (
              <GitPullRequest className="w-4 h-4 mr-2" />
            )}
            Clone
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Repository Status */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Repository Status
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Current git repository information
            </p>
          </div>
          {loading.status ? (
            <div className="px-4 py-5 flex justify-center">
              <div className="loading-spinner"></div>
            </div>
          ) : gitStatus ? (
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <GitBranch className="w-4 h-4 mr-2" />
                    Branch
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {gitStatus.branch}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Modified Files</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {gitStatus.modified?.length || 0}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Staged Files</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {gitStatus.staged?.length || 0}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Sync Status</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                    {gitStatus.ahead > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                        <ArrowUp className="w-3 h-3 mr-1" />
                        +{gitStatus.ahead}
                      </span>
                    )}
                    {gitStatus.behind > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <ArrowDown className="w-3 h-3 mr-1" />
                        -{gitStatus.behind}
                      </span>
                    )}
                    {gitStatus.ahead === 0 && gitStatus.behind === 0 && (
                      <span className="text-green-600">Up to date</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          ) : (
            <div className="px-4 py-5 text-center text-gray-500">
              Failed to load git status
            </div>
          )}
        </div>

        {/* Git Actions */}
        <div className="space-y-6">
          {/* Commit Changes */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Commit Changes
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Commit Message
                  </label>
                  <textarea
                    value={commitMessage}
                    onChange={(e) => setCommitMessage(e.target.value)}
                    rows={3}
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter commit message..."
                  />
                </div>
                <button
                  onClick={handleCommit}
                  disabled={!commitMessage.trim() || actionLoading === 'commit'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'commit' ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Commit Changes
                </button>
              </div>
            </div>
          </div>

          {/* Remote Operations */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Remote Operations
              </h3>
              <div className="flex space-x-3">
                <button
                  onClick={handlePull}
                  disabled={actionLoading === 'pull'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {actionLoading === 'pull' ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <ArrowDown className="w-4 h-4 mr-2" />
                  )}
                  Sync
                </button>
                <button
                  onClick={handlePush}
                  disabled={actionLoading === 'push'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {actionLoading === 'push' ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <ArrowUp className="w-4 h-4 mr-2" />
                  )}
                  Push
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commit History */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Recent Commits
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Latest commit history for the repository
          </p>
        </div>
        {loading.log ? (
          <div className="px-4 py-5 flex justify-center">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <ul role="list" className="divide-y divide-gray-200">
            {commits.map((commit) => (
              <li key={commit.hash} className="px-4 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <GitCommit className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {commit.message}
                      </div>
                      <div className="text-sm text-gray-500">
                        {commit.author_name} • {new Date(commit.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 font-mono">
                    {commit.hash.substring(0, 8)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Git Diff */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Current Diff
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                View current changes in the working directory
              </p>
            </div>
            <button
              onClick={loadDiff}
              disabled={loading.diff}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {loading.diff ? (
                <div className="loading-spinner mr-2"></div>
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Load Diff
            </button>
          </div>
        </div>
        {diff && (
          <div className="border-t border-gray-200">
            <pre className="px-4 py-5 text-sm text-gray-900 bg-gray-50 overflow-x-auto max-h-96">
              {diff}
            </pre>
          </div>
        )}
      </div>

      {/* Force Clone Confirmation Modal */}
      {showForceCloneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowForceCloneModal(false)}
          />
          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 z-10">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Repository already cloned</h3>
                <p className="text-sm text-gray-500">
                  A repository is already present at <code className="bg-gray-100 px-1 rounded text-xs font-mono">/app/tunistock</code>.<br /><br />
                  Do you want to <span className="font-semibold text-red-600">wipe it and re-clone</span>? This will permanently discard any uncommitted local changes.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowForceCloneModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                onClick={handleForceClone}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Yes, wipe &amp; re-clone
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GitManager;